'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { gsap } from 'gsap';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useHasMounted } from '@/hooks/use-has-mounted';
import { useAuth } from '@/hooks/use-auth';
import { useRegional } from '@/components/providers/regional-provider';
import { trpc } from '@/lib/trpc';
import { useDisplayCurrency } from '@/tenants/_shared/hooks';
import { useTeamDonationStrategy } from '@/tenants/_shared/hooks/donation-strategies';
import { getTenantConfig } from '@/tenants/config';
import Link from 'next/link';
import {
  MapPin,
  Calendar,
  Heart,
  TrendingUp,
  Award,
  Users,
  ChevronRight,
  CheckCircle2,
  Loader2,
  Copy,
  Check,
} from 'lucide-react';
import { type TeamPublicProfile } from '@impact/types';
import {
  DonationFormModal,
  DonationAmountCard,
  type DonationData,
} from '@/tenants/_shared/components';
import { usePaymentReturn } from '@/tenants/_shared/hooks/usePaymentReturn';
import { SponsorLogosSection } from '@/tenants/_shared/components/SponsorLogosSection';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog/dialog';
import { ShareDropdown } from '@/components/share-dropdown';

function toSlug(value: string) {
  return value
    .toLowerCase()
    .replace(/['']/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function isHtmlContent(content: string): boolean {
  return /<(p|br|div|h[1-6]|ul|ol|li|a|strong|em|b|i|span|img|blockquote|table)\b[^>]*\/?>/i.test(
    content,
  );
}

function stripHtml(input: string): string {
  return input
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function ShortUrlDisplay({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    void navigator.clipboard.writeText(`https://${url}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="flex items-center gap-1.5 mt-1 text-stone-500 hover:text-uwr-orange transition-colors group"
    >
      <span className="text-sm">{url}</span>
      {copied ? (
        <Check className="w-3.5 h-3.5 text-green-400" />
      ) : (
        <Copy className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
      )}
    </button>
  );
}

interface TeamProfilePageProps {
  handle?: string;
  profile?: TeamPublicProfile;
}

interface TeamDonor {
  id: string;
  name: string | null;
  anonymous: boolean;
  amount: number;
  message?: string;
  date: string;
}

export function TeamProfilePage({ handle, profile }: TeamProfilePageProps) {
  const t = useTranslations('fundraiser');
  const tMyTeam = useTranslations('my.team');
  const tCommon = useTranslations('common');
  const tDonate = useTranslations('donate.form');
  const router = useRouter();
  const utils = trpc.useUtils();
  const { isAuthenticated } = useAuth({ hydrateFromQuery: true });

  const mounted = useHasMounted();
  const { currency: selectedCurrency } = useRegional();

  const { data: fetchedProfile, isLoading } = trpc.publicTeam.byHandle.useQuery(
    { handle: handle ?? '', currencyCode: selectedCurrency },
    { enabled: !!handle, initialData: profile },
  );

  const team = fetchedProfile ?? profile ?? null;
  const currency = useDisplayCurrency();
  const { data: donorsData } = trpc.publicTeam.listDonors.useQuery(
    {
      teamId: team?.id ?? '',
      currencyCode: selectedCurrency,
    },
    { enabled: !!team?.id },
  );
  // Payment return detection (bank redirect return flow)
  const {
    returnDonationId,
    redirectResult,
    sessionId: returnSessionId,
    eventId: returnEventId,
    recoveryToken,
    isLocalStorageRecovery,
  } = usePaymentReturn();

  const donationEventId = team?.currentCampaign?.eventId || returnEventId || undefined;
  const strategy = useTeamDonationStrategy(donationEventId, team?.id);

  // Donation modal state — single modal instance for both mobile button and desktop card
  const [isDonateModalOpen, setIsDonateModalOpen] = useState(false);
  const [donateInitialAmount, setDonateInitialAmount] = useState<number | null>(null);

  const handleDonationSuccess = useCallback(
    (data: DonationData) => {
      if (!handle) return;
      const queryInput = { handle, currencyCode: selectedCurrency };

      // Convert donation amount from payment currency to display currency
      const displayFxRate = team?.fxRate ?? 1;
      const paymentFxRate = strategy.paymentConfig?.paymentConfigs[0]?.fxRate ?? 1;
      const convertedAmount =
        paymentFxRate > 0 ? data.amount * (displayFxRate / paymentFxRate) : data.amount;

      void utils.publicTeam.byHandle.cancel(queryInput);
      utils.publicTeam.byHandle.setData(queryInput, (old) => {
        if (!old) return old;
        const newTotal = old.totalRaised + convertedAmount;
        return {
          ...old,
          totalRaised: newTotal,
          donationCount: old.donationCount + 1,
          goalProgress:
            old.fundraisingGoal > 0 ? Math.min((newTotal / old.fundraisingGoal) * 100, 100) : 0,
        };
      });
    },
    [handle, selectedCurrency, utils, team?.fxRate, strategy.paymentConfig],
  );

  // Refetch profile after redirect payment success (zeroDrift gives correct converted total)
  const handleRedirectSuccess = () => {
    if (!handle) return;
    void utils.publicTeam.byHandle.invalidate({
      handle,
      currencyCode: selectedCurrency,
    });
    if (team?.id) {
      void utils.publicTeam.listDonors.invalidate({
        teamId: team.id,
        currencyCode: selectedCurrency,
      });
    }
  };

  const updates = team ? team.updates : [];
  const donors: TeamDonor[] = donorsData?.donors ?? [];
  const shouldCheckTeamState = isAuthenticated;
  const myTeamStateQuery = trpc.myTeam.state.useQuery(undefined, {
    enabled: shouldCheckTeamState,
    retry: false,
  });
  const joinTeamMutation = trpc.myTeam.joinTeam.useMutation({
    onSuccess: async () => {
      if (team?.handle) {
        await utils.publicTeam.byHandle.invalidate({
          handle: team.handle,
          currencyCode: selectedCurrency,
        });
      }
      await myTeamStateQuery.refetch();
      setIsJoinConfirmOpen(false);
    },
  });

  const progressPercent = team ? Math.min(team.goalProgress, 100) : 0;

  const [selectedPost, setSelectedPost] = useState<TeamPublicProfile['updates'][number] | null>(
    null,
  );
  const [blogDrawerOpen, setBlogDrawerOpen] = useState(false);
  const [isJoinConfirmOpen, setIsJoinConfirmOpen] = useState(false);

  const avatarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const ctx = gsap.context(() => {
      if (avatarRef.current) {
        gsap.from(avatarRef.current, {
          scale: 0.85,
          opacity: 0,
          duration: 0.8,
          ease: 'back.out(1.7)',
          delay: 0.2,
        });
      }
    });

    return () => ctx.revert();
  }, []);

  const formatCurrency = (amount: number) =>
    mounted ? `${currency.symbol}${amount.toLocaleString()}` : `${currency.symbol}${amount}`;

  const formatDate = (dateStr: string, options?: Intl.DateTimeFormatOptions) => {
    const date = new Date(dateStr);
    if (!mounted) {
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
        date.getDate(),
      ).padStart(2, '0')}`;
    }
    return date.toLocaleDateString('en-AU', options);
  };

  if (isLoading && !team) {
    return (
      <main className="flex-1 bg-white pb-20 md:pb-0">
        <section className="container max-w-5xl py-16">
          <p className="text-stone-500">{tCommon('loading')}</p>
        </section>
      </main>
    );
  }

  if (!team) {
    return (
      <main className="flex-1 bg-white pb-20 md:pb-0">
        <section className="container max-w-5xl py-16">
          <p className="text-stone-500">{tCommon('error')}</p>
        </section>
      </main>
    );
  }

  if (!team.currentCampaign?.campaignId || !team.currentCampaign?.eventId) {
    return (
      <main className="flex-1 bg-white pb-20 md:pb-0">
        <section className="container max-w-5xl py-16">
          <p className="text-stone-500">{tCommon('error')}</p>
        </section>
      </main>
    );
  }

  const teamInitials = team.name
    .split(' ')
    .map((token) => token[0])
    .join('')
    .slice(0, 2);
  const currentTeamId = myTeamStateQuery.data?.currentCampaign?.teamId ?? null;
  const currentTeamName = myTeamStateQuery.data?.memberTeam?.name ?? '';
  const isAlreadyOnTeam = Boolean(currentTeamId && currentTeamId === team.id);
  const isPrivateTeam = Boolean(team.currentCampaign?.isPrivate);
  const canSwapTeams = Boolean(
    myTeamStateQuery.data?.canChangeTeam && currentTeamId && currentTeamId !== team.id,
  );
  const canJoinTeam = Boolean(myTeamStateQuery.data?.canJoinTeam || canSwapTeams);

  const handleJoinTeam = async () => {
    if (!isAuthenticated) {
      router.push('/my/team/join');
      return;
    }

    if (isAlreadyOnTeam) {
      return;
    }

    if (isPrivateTeam) {
      router.push('/my/team/join');
      return;
    }

    if (canSwapTeams) {
      setIsJoinConfirmOpen(true);
      return;
    }

    if (canJoinTeam) {
      await joinTeamMutation.mutateAsync({ teamId: team.id });
      return;
    }

    router.push('/my/team/join');
  };

  return (
    <>
      <main className="flex-1 bg-white pb-20 md:pb-0">
        <section className="relative h-[300px] md:h-[400px]">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={team.bannerUrl || '/images/uwr/HeroImageUWR.jpg'}
            alt={team.name}
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-white via-white/50 to-transparent" />
        </section>

        <section className="relative -mt-32 md:-mt-24 pb-8 border-b border-stone-200">
          <div className="container max-w-5xl">
            <div className="flex flex-col md:flex-row gap-6 items-start">
              {/* Team Logo */}
              <div ref={avatarRef} className="relative w-36 h-36 md:w-44 md:h-44 flex-shrink-0">
                <div className="w-full h-full rounded-full ring-4 ring-uwr-orange overflow-hidden bg-stone-100">
                  {team.logoUrl ? (
                    <>
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src={team.logoUrl}
                        alt={team.name}
                        className="absolute inset-0 h-full w-full object-cover rounded-full"
                      />
                    </>
                  ) : team.currentCampaign?.eventThumbnailUrl ? (
                    <>
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src={team.currentCampaign.eventThumbnailUrl}
                        alt="Event thumbnail"
                        className="absolute inset-0 h-full w-full object-cover rounded-full"
                      />
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-4xl font-bold text-stone-500">
                      {teamInitials}
                    </div>
                  )}
                </div>
                <div className="absolute -bottom-1 -right-1 w-10 h-10 rounded-full bg-uwr-orange flex items-center justify-center border-2 border-white z-10">
                  <Users className="w-5 h-5 text-stone-950" />
                </div>
              </div>

              <div className="flex-1 pt-2 md:pt-8">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <h1 className="text-3xl md:text-4xl font-bold text-stone-900">{team.name}</h1>
                      <span className="px-2 py-0.5 rounded-full bg-uwr-orange/20 text-uwr-orange text-xs font-medium">
                        {t('team')}
                      </span>
                    </div>
                    <ShortUrlDisplay
                      url={`${getTenantConfig('uwr').teamShortUrlBase ?? 'gfolk.team'}/${team.handle}`}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    {!isAlreadyOnTeam && !isPrivateTeam && (
                      <Button
                        type="button"
                        onClick={() => void handleJoinTeam()}
                        disabled={
                          joinTeamMutation.isPending ||
                          (shouldCheckTeamState && myTeamStateQuery.isLoading)
                        }
                        className="bg-uwr-orange text-white hover:bg-uwr-orange-light"
                      >
                        {joinTeamMutation.isPending ? (
                          <span className="inline-flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            {tCommon('loading')}
                          </span>
                        ) : (
                          tMyTeam('joinTeam')
                        )}
                      </Button>
                    )}
                    <ShareDropdown
                      url={`${typeof window !== 'undefined' ? window.location.origin : ''}/team/${team.handle}`}
                      title={team.name}
                    />
                    {strategy.isFundraisingActive && (
                      <div className="lg:hidden">
                        <Button
                          onClick={() => {
                            setDonateInitialAmount(null);
                            setIsDonateModalOpen(true);
                          }}
                          className="bg-uwr-orange text-white h-9 hover:bg-uwr-orange-light font-semibold"
                        >
                          <Heart className="w-4 h-4 mr-2" />
                          {tDonate('donate')}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap gap-4 mt-4 text-sm">
                  {team.currentEvent && (
                    <div className="flex items-center gap-2 text-stone-500">
                      <MapPin className="w-4 h-4 text-uwr-orange" />
                      {team.currentCampaign?.eventSlug && team.currentEvent.country ? (
                        <Link
                          href={`/events/${toSlug(team.currentEvent.country)}/${team.currentCampaign.eventSlug}`}
                          className="hover:text-uwr-orange transition-colors"
                        >
                          {team.currentEvent.city}, {team.currentEvent.country}
                        </Link>
                      ) : (
                        <span>
                          {team.currentEvent.city}, {team.currentEvent.country}
                        </span>
                      )}
                    </div>
                  )}
                  {team.yearFounded && (
                    <div className="flex items-center gap-2 text-stone-500">
                      <Calendar className="w-4 h-4 text-uwr-orange" />
                      <span>
                        {t('founded')} {team.yearFounded}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-8 md:py-12">
          <div className="container max-w-5xl">
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-8">
                <div className="bg-white rounded-xl p-6 border border-stone-200">
                  <div className="flex justify-between items-end mb-3 gap-2">
                    <div className="min-w-0">
                      <p className="text-2xl font-bold text-uwr-orange">
                        {formatCurrency(team.totalRaised)}
                      </p>
                      <p className="text-stone-500 text-xs md:text-sm">
                        {t('goal')} {formatCurrency(team.fundraisingGoal)}
                        {team.goalSourceAmount && team.goalSourceCurrency ? (
                          <span className="ml-1 text-stone-500">
                            ({team.goalSourceCurrency} {team.goalSourceAmount.toLocaleString()})
                          </span>
                        ) : null}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xl md:text-2xl font-bold text-stone-900">
                        {Math.round(progressPercent)}%
                      </p>
                      <p className="text-stone-500 text-xs md:text-sm">
                        {team.donationCount} {t('supporters').toLowerCase()}
                      </p>
                    </div>
                  </div>
                  <div className="h-3 bg-stone-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-uwr-orange to-uwr-orange-light rounded-full transition-all duration-500"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                  <p className="text-stone-500 text-sm mt-2">
                    {t('percentOfGoal', { percent: Math.round(progressPercent) })}
                  </p>
                </div>

                {updates.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-semibold text-stone-900">{t('updates')}</h2>
                    </div>
                    <div className="space-y-4">
                      {updates.map((post) => (
                        <button
                          key={post.id}
                          type="button"
                          onClick={() => {
                            setSelectedPost(post);
                            setBlogDrawerOpen(true);
                          }}
                          className="w-full text-left bg-white rounded-xl border border-stone-200 hover:bg-stone-100/50 transition-colors overflow-hidden"
                        >
                          {post.imageUrl && (
                            // eslint-disable-next-line @next/next/no-img-element
                            <img
                              src={post.imageUrl}
                              alt={post.title}
                              className="w-full h-48 object-cover"
                            />
                          )}
                          <div className="p-6">
                            <p className="text-stone-500 text-sm mb-1">
                              {formatDate(post.createdAt, {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                              })}
                            </p>
                            <h3 className="text-stone-900 font-semibold mb-2">{post.title}</h3>
                            <p className="text-stone-500 text-sm line-clamp-3">
                              {post.content && isHtmlContent(post.content)
                                ? stripHtml(post.content)
                                : post.content}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-stone-900">
                      <Users className="w-5 h-5 inline mr-2 text-uwr-orange" />
                      {t('teamMembers')} ({team.members.length})
                    </h2>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {team.members.map((member) => (
                      <Link
                        key={`${member.entrantId}-${member.role}`}
                        href={member.username ? `/fundraiser/${member.username}` : '#'}
                        className={`rounded-xl p-4 border flex items-center gap-4 transition-colors ${
                          member.role === 'captain'
                            ? 'bg-uwr-orange/10 border-uwr-orange/40 hover:bg-uwr-orange/15'
                            : 'bg-white border-stone-200 hover:bg-stone-100/50'
                        }`}
                      >
                        <div className="w-12 h-12 rounded-full bg-stone-200 overflow-hidden">
                          {member.profileImageUrl ? (
                            <>
                              {/* eslint-disable-next-line @next/next/no-img-element */}
                              <img
                                src={member.profileImageUrl}
                                alt={`${member.firstName} ${member.lastName}`}
                                className="object-cover"
                              />
                            </>
                          ) : team.currentCampaign?.eventThumbnailUrl ? (
                            <>
                              {/* eslint-disable-next-line @next/next/no-img-element */}
                              <img
                                src={team.currentCampaign.eventThumbnailUrl}
                                alt={`${member.firstName} ${member.lastName}`}
                                className="w-full h-full object-cover"
                              />
                            </>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-sm font-medium text-stone-500">
                              {member.firstName[0]}
                              {member.lastName[0]}
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-stone-900 font-medium truncate">
                              {member.firstName} {member.lastName}
                            </p>
                            {member.role === 'captain' && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-uwr-orange/20 px-2 py-0.5 text-[11px] font-medium text-uwr-orange">
                                <CheckCircle2 className="h-3 w-3" />
                                {t('captain')}
                              </span>
                            )}
                          </div>
                          <p className="text-stone-500 text-sm">
                            {formatCurrency(member.totalRaised)} {t('raised')}
                          </p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-stone-600" />
                      </Link>
                    ))}
                  </div>
                </div>

                <div className="grid gap-4 grid-cols-2 md:grid-cols-3">
                  <div className="col-span-2 md:col-span-1 bg-white rounded-xl p-5 border border-stone-200 text-center">
                    <TrendingUp className="w-6 h-6 text-uwr-orange mx-auto mb-2" />
                    <p className="text-2xl font-bold text-stone-900">
                      {formatCurrency(team.totalRaised)}
                    </p>
                    <p className="text-stone-500 text-sm">{t('totalRaised')}</p>
                  </div>
                  <div className="bg-white rounded-xl p-5 border border-stone-200 text-center">
                    <Award className="w-6 h-6 text-uwr-orange mx-auto mb-2" />
                    <p className="text-2xl font-bold text-stone-900">{team.totalEvents}</p>
                    <p className="text-stone-500 text-sm">{t('events')}</p>
                  </div>
                  <div className="bg-white rounded-xl p-5 border border-stone-200 text-center">
                    <Users className="w-6 h-6 text-uwr-orange mx-auto mb-2" />
                    <p className="text-2xl font-bold text-stone-900">{team.memberCount}</p>
                    <p className="text-stone-500 text-sm">{t('members')}</p>
                  </div>
                </div>

                {donors.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-semibold text-stone-900">
                        <Heart className="w-5 h-5 inline mr-2 text-uwr-orange" />
                        {t('supporters')} ({donors.length})
                      </h2>
                    </div>
                    <div className="space-y-3">
                      {donors.map((donor) => (
                        <div
                          key={donor.id}
                          className="bg-white rounded-xl p-4 border border-stone-200 flex items-start gap-4"
                        >
                          <div className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center flex-shrink-0">
                            {donor.anonymous ? (
                              <Heart className="w-5 h-5 text-stone-500" />
                            ) : (
                              <span className="text-stone-500 font-medium">
                                {(donor.name ?? '')
                                  .split(' ')
                                  .filter(Boolean)
                                  .map((token: string) => token[0])
                                  .join('')}
                              </span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-stone-900 font-medium">
                                {donor.anonymous
                                  ? tCommon('anonymous')
                                  : (donor.name ?? tCommon('anonymous'))}
                              </p>
                              <p className="text-uwr-orange font-semibold">
                                {formatCurrency(Math.round(donor.amount))}
                              </p>
                            </div>
                            {donor.message && (
                              <p className="text-stone-500 text-sm mt-1">
                                &ldquo;{donor.message}&rdquo;
                              </p>
                            )}
                            <p className="text-stone-600 text-xs mt-1">
                              {formatDate(donor.date, {
                                month: 'short',
                                day: 'numeric',
                              })}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Event Sponsors */}
                <SponsorLogosSection eventId={donationEventId} variant="dark" />
              </div>

              <div className="hidden lg:block lg:col-span-1">
                <div className="sticky top-24">
                  {strategy.isFundraisingActive ? (
                    <DonationAmountCard
                      recipientName={team.name}
                      currency={strategy.currency}
                      onDonate={(amount) => {
                        setDonateInitialAmount(amount);
                        setIsDonateModalOpen(true);
                      }}
                    />
                  ) : (
                    <div className="bg-white rounded-xl p-6 border border-stone-200 text-center">
                      <p className="text-stone-500 text-sm">{tDonate('fundraisingDisabled')}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {blogDrawerOpen && selectedPost && (
          <div
            className="fixed inset-0 z-50 bg-black/80 flex items-end"
            onClick={() => setBlogDrawerOpen(false)}
          >
            <div
              className="bg-white border-t border-stone-200 w-full max-h-[85vh] rounded-t-2xl p-6 overflow-y-auto"
              onClick={(event) => event.stopPropagation()}
            >
              <p className="text-stone-500 text-sm mb-2">
                {formatDate(selectedPost.createdAt, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
              <h2 className="text-2xl font-bold text-stone-900 mb-4">{selectedPost.title}</h2>
              {selectedPost.imageUrl && (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={selectedPost.imageUrl}
                  alt={selectedPost.title}
                  className="w-full max-h-[400px] object-cover rounded-xl mb-4"
                />
              )}
              {selectedPost.updateType === 'video' && selectedPost.youtubeEmbedUrl && (
                <div className="relative w-full pb-[56.25%] mb-4">
                  <iframe
                    src={selectedPost.youtubeEmbedUrl}
                    title={selectedPost.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full rounded-xl"
                  />
                </div>
              )}
              {selectedPost.content && isHtmlContent(selectedPost.content) ? (
                mounted ? (
                  <div
                    className="prose max-w-none prose-p:text-stone-600 prose-headings:text-stone-900 prose-a:text-blue-600 prose-strong:text-stone-900"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedPost.content) }}
                  />
                ) : (
                  <div className="prose max-w-none" />
                )
              ) : (
                <p className="text-stone-600 leading-relaxed whitespace-pre-wrap">
                  {selectedPost.content}
                </p>
              )}
            </div>
          </div>
        )}
      </main>

      <DonationFormModal
        open={isDonateModalOpen}
        onOpenChange={(open) => {
          setIsDonateModalOpen(open);
          if (!open) setDonateInitialAmount(null);
        }}
        initialAmount={donateInitialAmount}
        recipientName={team.name}
        strategy={strategy}
        onSubmit={handleDonationSuccess}
        returnDonationId={returnDonationId ?? undefined}
        redirectResult={redirectResult}
        returnSessionId={returnSessionId}
        recoveryToken={recoveryToken}
        isLocalStorageRecovery={isLocalStorageRecovery}
        onRedirectSuccess={handleRedirectSuccess}
      />

      <Dialog open={isJoinConfirmOpen} onOpenChange={setIsJoinConfirmOpen}>
        <DialogContent className="max-w-md border-stone-300 bg-white text-stone-900">
          <DialogHeader>
            <DialogTitle>{t('joinTeamConfirmTitle')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-stone-600">
            {t('joinTeamConfirmDescription', {
              currentTeam: currentTeamName,
              newTeam: team.name,
            })}
          </p>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setIsJoinConfirmOpen(false)}
              disabled={joinTeamMutation.isPending}
              className="text-stone-600 hover:bg-stone-100 hover:text-stone-900"
            >
              {tCommon('cancel')}
            </Button>
            <Button
              type="button"
              onClick={() => joinTeamMutation.mutate({ teamId: team.id })}
              disabled={joinTeamMutation.isPending}
              className="bg-uwr-orange text-white hover:bg-uwr-orange-light"
            >
              {joinTeamMutation.isPending ? tCommon('loading') : t('joinTeamConfirmCta')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
